import React, { useState } from 'react';
import { Modal, Form, message } from 'antd';
import { ProForm, ProFormText } from '@ant-design/pro-components';
import { history, useModel, useIntl } from 'umi';
import { changePassword } from '@/services/user';
import { authManager, AuthSystem } from '@/services/auth/authManager';

interface ChangePasswordModalProps {
  visible: boolean;
  onCancel: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ visible, onCancel }) => {
  const intl = useIntl();
  const { setInitialState } = useModel('@@initialState');
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const handleSubmit = async (values: any) => {
    setLoading(true);
    const hide = message.loading(
      intl.formatMessage({
        id: 'common.processing',
        defaultMessage: '处理中...',
      }),
    );

    try {
      await changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });

      hide();
      message.success(
        intl.formatMessage({
          id: 'user.changePassword.success',
          defaultMessage: '密码修改成功',
        }),
      );
      // Reset form and close modal
      form.resetFields();
      onCancel();

      // The backend invalidates the current session in the same transaction as
      // the password change. Clear the browser state without making a logout
      // call that is guaranteed to be rejected by the now-invalid token.
      authManager.clearAuth(AuthSystem.ADMIN);
      setInitialState((s: any) => ({ ...s, currentUser: undefined }));
      history.replace('/user/login');
    } catch (error: any) {
      hide();

      // Handle specific error messages
      if (error?.response?.data?.message === 'Current password is incorrect') {
        message.error(
          intl.formatMessage({
            id: 'user.changePassword.incorrectPassword',
            defaultMessage: '当前密码不正确',
          }),
        );
      } else {
        message.error(
          intl.formatMessage({
            id: 'user.changePassword.failed',
            defaultMessage: '密码修改失败',
          }),
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={intl.formatMessage({
        id: 'user.changePassword.title',
        defaultMessage: '更改密碼',
      })}
      open={visible}
      onCancel={onCancel}
      footer={null}
      confirmLoading={loading}
      width={480}
      centered
    >
      <ProForm
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
        submitter={{
          searchConfig: {
            submitText: intl.formatMessage({
              id: 'common.submit',
              defaultMessage: '提交',
            }),
            resetText: intl.formatMessage({
              id: 'common.cancel',
              defaultMessage: '取消',
            }),
          },
          onReset: onCancel,
          submitButtonProps: {
            loading,
            size: 'large',
          },
          resetButtonProps: {
            size: 'large',
          },
        }}
      >
        <ProFormText.Password
          name="currentPassword"
          label={intl.formatMessage({
            id: 'user.changePassword.currentPassword',
            defaultMessage: '当前密码',
          })}
          rules={[
            {
              required: true,
              message: intl.formatMessage({
                id: 'user.changePassword.currentPassword.required',
                defaultMessage: '请输入当前密码',
              }),
            },
          ]}
          fieldProps={{
            autoComplete: 'current-password',
            size: 'large',
            style: { marginBottom: 16 },
          }}
        />

        <ProFormText.Password
          name="newPassword"
          label={intl.formatMessage({
            id: 'user.changePassword.newPassword',
            defaultMessage: '新密码',
          })}
          rules={[
            {
              required: true,
              message: intl.formatMessage({
                id: 'user.changePassword.newPassword.required',
                defaultMessage: '请输入新密码',
              }),
            },
            {
              min: 8,
              message: intl.formatMessage({
                id: 'user.changePassword.newPassword.minLength',
                defaultMessage: '密码至少8个字符',
              }),
            },
            {
              pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]/,
              message: intl.formatMessage({
                id: 'user.changePassword.newPassword.pattern',
                defaultMessage: '密码必须包含大写字母、小写字母和数字',
              }),
            },
          ]}
          fieldProps={{
            autoComplete: 'new-password',
            size: 'large',
            style: { marginBottom: 16 },
          }}
        />

        <ProFormText.Password
          name="confirmPassword"
          label={intl.formatMessage({
            id: 'user.changePassword.confirmPassword',
            defaultMessage: '确认新密码',
          })}
          dependencies={['newPassword']}
          rules={[
            {
              required: true,
              message: intl.formatMessage({
                id: 'user.changePassword.confirmPassword.required',
                defaultMessage: '请确认新密码',
              }),
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error(
                    intl.formatMessage({
                      id: 'user.changePassword.confirmPassword.mismatch',
                      defaultMessage: '两次密码输入不一致',
                    }),
                  ),
                );
              },
            }),
          ]}
          fieldProps={{
            autoComplete: 'new-password',
            size: 'large',
            style: { marginBottom: 24 },
          }}
        />
      </ProForm>
    </Modal>
  );
};

export default ChangePasswordModal;
